/**
 * Registers uploaded files in small chunks so one mutation stays well inside
 * Convex's 1 s limit (#5918). A chunk that fails is retried after a backoff
 * at half its size, down to single files; a single file is retried a few
 * times before it is reported failed. Registration is idempotent per storage
 * key on the server, so a retry after a timeout that actually committed
 * returns the same file. Nothing is dropped: every item is either registered
 * or returned in `failed` with its last error.
 */
export const REGISTER_CHUNK_SIZE = 5;
const SINGLE_FILE_ATTEMPTS = 3;
/** Wait before retry N of a single file (N = 1, 2) and before a split. */
const RETRY_BACKOFF_MS = [400, 1_200, 3_000];

/** A register reply that left out some of the files it was sent. */
export class RegisterResultMissingError extends Error {
  constructor(readonly missing: readonly unknown[]) {
    super('REGISTER_RESULT_MISSING');
  }
}

/** Errors that a retry cannot fix: report the file at once. */
const PERMANENT_ERROR_CODES = [
  'VFS_PERMISSION_DENIED',
  'WORKSPACE_UPLOAD_UNSUPPORTED_TYPE',
  'WORKSPACE_UPLOAD_NOT_OWNED',
  'WORKSPACE_UPLOAD_ALREADY_MATERIALIZED',
  // The person chose not to keep both after the file to replace changed.
  'WORKSPACE_UPLOAD_REPLACE_CONFLICT',
  'WORKSPACE_UPLOAD_ORG_MISMATCH',
  'WORKSPACE_STORAGE_LIMIT_EXCEEDED',
  'WORKSPACE_TARGET_NOT_DIRECTORY',
  'VFS_STORAGE_BINDING_INVALID',
];

function errorText(error: unknown): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data: unknown }).data;
    if (typeof data === 'string') return data;
  }
  return error instanceof Error ? error.message : String(error);
}

export function isPermanentRegisterError(error: unknown): boolean {
  const text = errorText(error);
  return PERMANENT_ERROR_CODES.some((code) => text.includes(code));
}

export type RegisterInChunksResult<T, R> = {
  results: R[];
  failed: Array<{ item: T; error: unknown }>;
};

export async function registerInChunks<T, R>(
  items: readonly T[],
  register: (chunk: T[]) => Promise<R[]>,
  options: {
    /** Pairs each reply row with the item it registered. */
    itemKey: (item: T) => string;
    resultKey: (result: R) => string;
    chunkSize?: number;
    sleep?: (ms: number) => Promise<void>;
    signal?: AbortSignal;
  }
): Promise<RegisterInChunksResult<T, R>> {
  const chunkSize = Math.max(1, options.chunkSize ?? REGISTER_CHUNK_SIZE);
  const sleep =
    options.sleep ??
    ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const queue: T[][] = [];
  for (let start = 0; start < items.length; start += chunkSize)
    queue.push(items.slice(start, start + chunkSize));
  const results: R[] = [];
  const failed: Array<{ item: T; error: unknown }> = [];
  const singleAttempts = new Map<T, number>();

  while (queue.length > 0) {
    const chunk = queue.shift() as T[];
    if (options.signal?.aborted) {
      for (const item of [chunk, ...queue].flat())
        failed.push({
          item,
          error: new DOMException('Cancelled', 'AbortError'),
        });
      break;
    }
    try {
      const reply = await register(chunk);
      // Only rows that answer an item in this chunk count; an item the
      // reply left out is a failure of that item, never a silent success.
      const sent = new Set(chunk.map(options.itemKey));
      const answered = new Set<string>();
      for (const row of reply) {
        const key = options.resultKey(row);
        if (!sent.has(key) || answered.has(key)) continue;
        answered.add(key);
        results.push(row);
      }
      const missing = chunk.filter(
        (item) => !answered.has(options.itemKey(item))
      );
      if (missing.length > 0) throw new RegisterResultMissingError(missing);
    } catch (error) {
      // Retry only what is still unregistered: the whole chunk after an
      // error, or just the items a short reply left out.
      const missing =
        error instanceof RegisterResultMissingError
          ? (error.missing as T[])
          : chunk;
      if (missing.length > 1) {
        // Isolate the cause: split and try each half on its own.
        const half = Math.ceil(missing.length / 2);
        await sleep(RETRY_BACKOFF_MS[0] ?? 400);
        queue.unshift(missing.slice(0, half), missing.slice(half));
        continue;
      }
      const [item] = missing as [T];
      const attempt = (singleAttempts.get(item) ?? 0) + 1;
      singleAttempts.set(item, attempt);
      if (isPermanentRegisterError(error) || attempt >= SINGLE_FILE_ATTEMPTS) {
        failed.push({ item, error });
        continue;
      }
      await sleep(RETRY_BACKOFF_MS[attempt - 1] ?? 3_000);
      queue.unshift([item]);
    }
  }
  return { results, failed };
}
